/**
 * CellDetailPopover
 *
 * Popover that opens when the user taps an occupied storage cell.
 * Shows the stacked resources and offers contextual actions.
 *
 * Resource display order in the popover mirrors the backend array:
 *   effectiveResources[0] = top resource (Part/Tool/RawMaterial) — shown first
 *   effectiveResources[1] = base Fixture                         — shown second with LayersRounded icon
 *
 * Actions (rows):
 *   Row 0 – Unmount part  (orange, only when stacked ×2 and top is non-Fixture)
 *           Removes ONLY the top resource; Fixture remains in the position.
 *   Row 1 – Remove | Move/Undo
 *           Remove: clears the ENTIRE position (all resources).
 *           Move: starts move mode (status bar shows "Moviendo: …").
 *           Undo: reverts the pending change (restores previous state).
 *   Row 2 – Move to In/Out | Cancel in/out movement
 *           Uses commandMove/request/fixture endpoint (moves the Fixture + contents
 *           to a designated In/Out storage). Only shown when no pending change and
 *           position.storageIsInOut === false.
 *
 * Props:
 *   anchorEl          – DOM element the popover anchors to
 *   position          – full position object from shelveStoragePositionsManager
 *                       (includes commandId, storageIsInOut, resources[], etc.)
 *   effectiveResources – resource[] after applying pending change (passed from parent)
 *   hasPending        – boolean: whether this position has a pending change
 *   onClose           – () => void
 *   onRemove          – (posId) => void
 *   onUnmount         – (posId) => void
 *   onUndo            – (posId) => void
 *   onMove            – (posId, topResource) => void
 *   onMoveToInOut     – (posId) => void
 *   onCancelInOut     – (commandId) => void
 */
import { Box, Typography, Button, Stack, Divider, Popover, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DeleteOutlineRounded, DriveFileMoveOutlined, LayersRounded, CategoryRounded, Grid3x3Rounded, HandymanRounded, ScatterPlotRounded, FileDownloadOutlined, CloseOutlined } from '@mui/icons-material'
import { TYPE_META, PENDING_COLOR, styles } from './warehouseStorageStyles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { fabricationOrderName } from '../generic/utilities/fabricationOrderName'

export default function CellDetailPopover ({ anchorEl, position, effectiveResources, hasPending, onClose, onRemove, onUnmount, onUndo, onMove, onMoveToInOut, onCancelInOut }) {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const topResource = effectiveResources[0]
  // The In/Out action moves a Fixture, so only offer it when the position actually holds one.
  const hasFixture = effectiveResources.some(r => (r.typeKey || r.type?.name || '') === 'Fixture')

  const typeIcon = (typeKey, color) => {
    const sx = { fontSize: 16, color, flexShrink: 0 }
    if (typeKey === 'Fixture') return <Grid3x3Rounded sx={sx} />
    if (typeKey === 'Tool') return <HandymanRounded sx={sx} />
    if (typeKey === 'RawMaterial') return <ScatterPlotRounded sx={sx} />
    return <CategoryRounded sx={sx} />
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: styles.popoverPaper }}
    >
      {position && (
        <Box>
          <Typography sx={{ fontSize: '0.78rem', color: accent, fontWeight: 600, mb: 1.5 }}>
            {t_('Shelf')} {position.shelve} · {t_('Row')} {position.position} · {t_('Col')} {position.depth}
          </Typography>

          <Stack spacing={0.8}>
            {effectiveResources.map((res, idx) => {
              // Normalise: backend may return "Raw Material" (with space) → "RawMaterial"
              const typeKey = (res.typeKey || res.type?.name || '').replace(/\s+/g, '')
              const typeColor = TYPE_META[typeKey]?.color || '#9e9e9e'
              const typeAbbr = typeKey === 'RawMaterial' ? 'RM' : (typeKey?.[0] || '?')
              const displayName = res.name || res.code || '—'
              const displayCode = res.name && res.code ? res.code : ''
              // Only a part has an order; the fixture underneath it gets '' and no line at all.
              const orderName = fabricationOrderName(res)

              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.2,
                    py: 1,
                    bgcolor: alpha(typeColor, 0.07),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(typeColor, 0.2)}`
                  }}
                >
                  {/* Type icon for top resource; stack icon for base layers */}
                  {idx === 0
                    ? typeIcon(typeKey, typeColor)
                    : <LayersRounded sx={{ fontSize: 16, color: PENDING_COLOR, flexShrink: 0 }} />}

                  {/* Type badge */}
                  <Box sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: typeColor,
                    bgcolor: alpha(typeColor, 0.15),
                    px: 0.7,
                    py: 0.3,
                    borderRadius: 0.8,
                    flexShrink: 0,
                    minWidth: 22,
                    textAlign: 'center'
                  }}
                  >
                    {typeAbbr}
                  </Box>

                  {/* Name + code */}
                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <Typography sx={{
                      fontSize: '0.82rem',
                      fontWeight: 500,
                      color: '#ddd',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    >
                      {displayName}
                    </Typography>
                    {displayCode && (
                      <Typography sx={{ fontSize: '0.68rem', color: alpha(typeColor, 0.8), lineHeight: 1.2 }}>
                        {displayCode}
                      </Typography>
                    )}
                    {orderName && (
                      <Typography
                        data-testid={`cell-resource-fabrication-order-${idx}`}
                        sx={{ fontSize: '0.68rem', color: '#9a9a9a', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {t_('Fabrication order')}: {orderName}
                      </Typography>
                    )}
                  </Box>

                  {/* Type label */}
                  <Typography sx={{ fontSize: '0.68rem', color: alpha(typeColor, 0.7), flexShrink: 0 }}>
                    {t_(typeKey)}
                  </Typography>
                </Box>
              )
            })}
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.07)' }} />

          <Stack spacing={1}>
            {/* Unmount — only when stacked (Part/Tool/RM on top of Fixture) and no pending */}
            {!hasPending && effectiveResources.length > 1 &&
              effectiveResources[0] && (effectiveResources[0].typeKey || effectiveResources[0].type?.name) !== 'Fixture' && (
                <Button
                  fullWidth
                  variant='outlined'
                  size='small'
                  startIcon={<LayersRounded />}
                  onClick={() => onUnmount(position.id)}
                  sx={{
                    minHeight: 48,
                    color: '#ff9800',
                    borderColor: 'rgba(255,152,0,0.35)',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.05)', borderColor: '#ff9800' }
                  }}
                >
                  {t_('Unmount part')}
                </Button>
            )}

            {/* Row 1: Remove + Move/Undo */}
            <Stack direction='row' spacing={1}>
              <Button
                fullWidth
                variant='contained'
                color='error'
                size='small'
                startIcon={<DeleteOutlineRounded />}
                onClick={() => onRemove(position.id)}
                sx={{ minHeight: 48, fontWeight: 600 }}
              >
                {t_('Remove')}
              </Button>
              {!hasPending && topResource && (
                <Button
                  fullWidth
                  variant='outlined'
                  size='small'
                  startIcon={<DriveFileMoveOutlined />}
                  onClick={() => onMove(position.id, topResource)}
                  sx={{
                    minHeight: 48,
                    color: accent,
                    borderColor: alpha(accent, 0.35),
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: alpha(accent, 0.05), borderColor: accent }
                  }}
                >
                  {t_('Move')}
                </Button>
              )}
              {hasPending && (
                <Button
                  fullWidth
                  variant='outlined'
                  size='small'
                  onClick={() => onUndo(position.id)}
                  sx={{
                    minHeight: 48,
                    color: PENDING_COLOR,
                    borderColor: 'rgba(255,152,0,0.35)',
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.05)', borderColor: PENDING_COLOR }
                  }}
                >
                  {t_('Undo')}
                </Button>
              )}
            </Stack>

            {/* Row 2: In/Out — only when no pending changes and position has resources.
                The place action requires a Fixture (it moves the fixture + contents); the
                cancel action stays available whenever an in/out command is in flight. */}
            {!hasPending && effectiveResources.length > 0 && !position.storageIsInOut && (position.commandId || hasFixture) && (
              position.commandId
                ? (
                  <Button
                    fullWidth
                    variant='outlined'
                    size='small'
                    color='warning'
                    startIcon={<CloseOutlined />}
                    onClick={() => onCancelInOut(position.commandId)}
                    sx={{ minHeight: 48, fontWeight: 600 }}
                  >
                    {t_('Cancel in/out movement')}
                  </Button>
                  )
                : (
                  <Button
                    fullWidth
                    variant='outlined'
                    size='small'
                    startIcon={<FileDownloadOutlined />}
                    onClick={() => onMoveToInOut(position.id)}
                    sx={{
                      minHeight: 48,
                      color: accent,
                      borderColor: alpha(accent, 0.35),
                      '&:hover': { bgcolor: alpha(accent, 0.05), borderColor: accent }
                    }}
                  >
                    {t_('Move to In/Out')}
                  </Button>
                  )
            )}
          </Stack>
        </Box>
      )}
    </Popover>
  )
}
